import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EPL_TEAMS } from "@/data/merchCups";
import { describeCupLine, formatKes, normalizeCupLine } from "@/lib/merchPricing";
import { getMerchOrder } from "@/lib/merchApi";
import { sendMerchWhatsAppReceipt } from "@/lib/whatsappReceipt";
import { markMerchOrderPaidSupabase } from "@/lib/merchOrdersSupabase";
import {
    supabase,
    isSupabaseConfigured,
    sanitizeSupabaseError,
} from "@/utils/supabase/client";

const MPESA_AUTH_URL =
    process.env.REACT_APP_MPESA_AUTH_URL ||
    "https://cheppar.co.ke/cheppar/authPay.php";

const PHONE_REGEX = /^(?:\+254|0)7\d{8}$/;

export default function MerchOrderPay() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [showPaymentSection, setShowPaymentSection] = useState(false);
    const pollActiveRef = useRef(true);

    const load = useCallback(async () => {
        try {
            const data = await getMerchOrder(orderId);
            setOrder(data);
            setPhoneNumber(data.phone || "");
            if (data.status === "paid") {
                navigate(`/merch/order/${data.id}/confirm`, { replace: true });
            }
        } catch {
            toast.error("Order not found.");
        } finally {
            setLoading(false);
        }
    }, [orderId, navigate]);

    useEffect(() => {
        pollActiveRef.current = true;
        load();
        return () => {
            pollActiveRef.current = false;
        };
    }, [load]);

    const handleCheckout = () => {
        if (!order?.external_reference) {
            setError(
                "This order is missing a payment reference. Please place a new order."
            );
            return;
        }
        if (!isSupabaseConfigured()) {
            setError(
                "Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY."
            );
            return;
        }
        setError(null);
        setShowPaymentSection(true);
    };

    const pollPaymentStatus = useCallback(
        async (userReference, retryCount = 15, interval = 10000) => {
            if (!pollActiveRef.current) return;

            if (retryCount <= 0) {
                setIsProcessing(false);
                setError(
                    "Payment verification timed out. Please check your M-Pesa balance or try again."
                );
                return;
            }

            try {
                const { data: paymentData, error: paymentError } = await supabase
                    .from("gaspayments")
                    .select("status, mpesa_reference")
                    .eq("user_reference", userReference)
                    .single();

                if (paymentError || !paymentData) {
                    window.setTimeout(() => {
                        pollPaymentStatus(userReference, retryCount - 1, interval);
                    }, interval);
                    return;
                }

                if (paymentData.status === true) {
                    setIsProcessing(false);
                    const mpesaReference = paymentData.mpesa_reference;

                    const updated = await markMerchOrderPaidSupabase(
                        userReference,
                        mpesaReference
                    );
                    const orderForReceipt = {
                        ...updated,
                        mpesa_receipt: mpesaReference,
                    };

                    try {
                        await sendMerchWhatsAppReceipt(orderForReceipt);
                        setMessage(
                            "Payment received — receipt sent to your WhatsApp."
                        );
                        toast.success("Payment confirmed! Check WhatsApp for your receipt.");
                    } catch (receiptErr) {
                        console.error("WhatsApp receipt failed:", receiptErr);
                        setMessage(
                            "Payment received. Receipt could not be sent automatically — we will contact you on WhatsApp."
                        );
                        toast.success("Payment confirmed!");
                        toast.error(
                            receiptErr.message ||
                                "WhatsApp receipt failed. Contact support with your order ID."
                        );
                    }
                    window.setTimeout(() => {
                        navigate(`/merch/order/${orderId}/confirm`);
                    }, 2000);
                    return;
                }

                window.setTimeout(() => {
                    pollPaymentStatus(userReference, retryCount - 1, interval);
                }, interval);
            } catch (pollErr) {
                setIsProcessing(false);
                setError(
                    pollErr.message ||
                        "There was an error checking your payment status."
                );
            }
        },
        [navigate, orderId]
    );

    const handlePayment = async () => {
        if (!phoneNumber.trim()) {
            setError("Please enter your phone number.");
            return;
        }

        if (!PHONE_REGEX.test(phoneNumber.replace(/\s/g, ""))) {
            setError(
                "Please enter a valid Kenyan phone number (e.g., 0722XXXXXX or +254722XXXXXX)"
            );
            return;
        }

        if (!order?.external_reference) {
            setError(
                "This order is missing a payment reference. Please place a new order."
            );
            return;
        }

        setIsProcessing(true);
        setMessage(null);
        setError(null);

        const paymentData = {
            phone_number: phoneNumber.startsWith("0")
                ? `+254${phoneNumber.slice(1)}`
                : phoneNumber,
            amount: order.total_kes,
            external_reference: order.external_reference,
        };

        try {
            const response = await fetch(MPESA_AUTH_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(paymentData),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `HTTP error! Status: ${response.status}, Response: ${errorText}`
                );
            }

            const responseData = await response.json();
            if (!responseData.success || responseData.status !== "QUEUED") {
                throw new Error(
                    responseData.message || "Payment initiation failed."
                );
            }

            setMessage("Please wait for an M-Pesa prompt.");

            const { error: supabaseError } = await supabase
                .from("merch_orders")
                .update({
                    phone: phoneNumber,
                    status: "pending",
                })
                .eq("external_reference", order.external_reference);

            if (supabaseError) {
                throw new Error(
                    sanitizeSupabaseError(supabaseError, "Failed to update order."),
                );
            }

            window.setTimeout(() => {
                pollPaymentStatus(order.external_reference);
            }, 5000);
        } catch (err) {
            setIsProcessing(false);
            setError(
                err.message ||
                    "There was an error processing your payment request."
            );
        }
    };

    if (loading) {
        return (
            <main className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
                <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            </main>
        );
    }

    if (!order) {
        return (
            <main className="min-h-screen bg-[#FAFAFA] px-6 pt-32 text-center">
                <p className="text-zinc-600">Order not found.</p>
                <Link
                    to="/merch/cups"
                    className="text-[#E54D2E] underline mt-4 inline-block"
                >
                    Back to cups
                </Link>
            </main>
        );
    }

    return (
        <main
            data-testid="merch-pay-page"
            className="min-h-screen bg-[#FAFAFA] text-zinc-950"
        >
            <Header />
            <div className="pt-24 sm:pt-28 pb-16 mx-auto max-w-lg px-4 sm:px-6">
                <div className="rounded-2xl border border-zinc-200 bg-white/90 p-6 shadow-md backdrop-blur-sm">
                    <h1 className="font-display text-2xl font-black text-center mb-6">
                        Complete your order
                    </h1>

                    <form
                        onSubmit={(e) => e.preventDefault()}
                        className="space-y-4"
                    >
                        <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 space-y-2 text-sm">
                            <p>
                                <span className="text-zinc-500">Order</span>{" "}
                                <span className="font-mono font-semibold">
                                    {order.id}
                                </span>
                            </p>
                            <p>
                                {order.cups} cups · {order.color} ·{" "}
                                {order.packs} pack{order.packs !== 1 ? "s" : ""}
                            </p>
                            <ul className="text-zinc-700 space-y-0.5">
                                {(order.cup_teams || []).map((entry, i) => (
                                    <li key={i}>
                                        Cup {i + 1}:{" "}
                                        {describeCupLine(
                                            normalizeCupLine(entry),
                                            EPL_TEAMS
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <p className="text-lg font-medium text-zinc-800">
                                Total amount
                            </p>
                            <p className="text-zinc-600">
                                {formatKes(order.total_kes)}
                            </p>
                        </div>

                        {order.status === "pending" && !showPaymentSection && (
                            <Button
                                type="button"
                                onClick={handleCheckout}
                                disabled={isProcessing}
                                className="w-full rounded-full bg-zinc-950 text-white hover:bg-[#E54D2E]"
                                style={{ border: "0.5px solid #22c55e" }}
                            >
                                Checkout
                            </Button>
                        )}

                        {order.status === "pending" && showPaymentSection && (
                            <div className="space-y-4">
                                <p className="text-sm text-zinc-600 text-center">
                                    An M-Pesa prompt of{" "}
                                    <span className="font-bold text-zinc-950">
                                        {order.total_kes.toLocaleString("en-KE")}
                                    </span>{" "}
                                    KES will appear.
                                </p>
                                <div>
                                    <Label
                                        htmlFor="phone"
                                        className="text-sm font-medium text-zinc-700"
                                    >
                                        Phone number (M-Pesa)
                                    </Label>
                                    <Input
                                        id="phone"
                                        type="tel"
                                        placeholder="e.g., 0722XXXXXX"
                                        value={phoneNumber}
                                        onChange={(e) =>
                                            setPhoneNumber(e.target.value)
                                        }
                                        required
                                        disabled={isProcessing}
                                        className="mt-1 w-full"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    onClick={handlePayment}
                                    disabled={isProcessing}
                                    className="w-full rounded-full bg-[#16A34A] text-white hover:bg-zinc-950"
                                    style={{ border: "0.5px solid #22c55e" }}
                                >
                                    {isProcessing
                                        ? "Processing..."
                                        : "Pay with M-Pesa"}
                                </Button>
                            </div>
                        )}

                        {message && (
                            <p className="text-green-600 text-sm text-center">
                                {message}
                            </p>
                        )}
                        {error && (
                            <p className="text-red-600 text-sm text-center">
                                {error}
                            </p>
                        )}
                        {isProcessing && (
                            <div className="flex justify-center">
                                <div className="w-6 h-6 border-4 border-t-green-500 border-zinc-200 rounded-full animate-spin" />
                            </div>
                        )}

                        <p className="text-xs text-zinc-500 text-center">
                            M-Pesa only. Receipt sent to WhatsApp when paid.
                        </p>
                    </form>
                </div>

                <Link
                    to="/merch/cups"
                    className="block text-center text-sm text-zinc-500 mt-8 hover:text-zinc-950"
                >
                    ← Order more cups
                </Link>
            </div>
            <Footer />
        </main>
    );
}
